// Types
import { RootState } from "@/redux/store";
import { IPageParam } from "@/Common/api/types/pagination";
import { GraphqlSubscriptionResponse } from "@/Common/api/types/GraphqlSubscriptionResponse";

// Hooks
import { useSelector } from "react-redux";
import { usePaginatedResource } from "@/Common/hooks/Pagination";
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";
import { buildGetNextPageParam } from "@/Common/hooks/Pagination/helpers";

// Queries
import { getAdvisorPerformance } from "@/DataPages/api/advisorPerformance/queries";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { onUpdateAdvisorPerformance } from "@/DataPages/api/advisorPerformance/subscriptions";

const PAGINATION_SIZE = 500;

interface IUseAdvisorPerformanceQueryConfiguration {
	enabled?: boolean;
	locationId?: string;
	commissionCycleId?: string;
	payPeriodId?: string;
	setIngesting: (value: boolean) => void;
}

interface IAdvisorPerformanceResponse {
	locationId?: string;
	commissionCycleId?: string;
	payPeriodId?: string;
}

/**
 * Custom hook to fetch advisor performance data.
 * @param enabled - enable/disable the query
 * @param payPeriodId - pay period id
 * @returns
 */
export const useAdvisorPerformanceQuery = ({
	enabled = true,
	commissionCycleId,
	payPeriodId,
	setIngesting,
}: IUseAdvisorPerformanceQueryConfiguration) => {
	const locationId = useSelector((state: RootState) => state.location.location?.locationId);

	const { addSubscription, getSubscription } = useSubscriptions();
	const queryClient = useQueryClient();

	const { data: advisorPerformance, ...otherResults } = usePaginatedResource({
		queryKey: ["advisorPerformance", locationId, commissionCycleId, payPeriodId],
		queryFn: async ({ pageParam }) => {
			if (!locationId || !commissionCycleId || !payPeriodId) {
				throw new Error("Cant request Advisor Performance without all the required parameters");
			}

			const typedPageParam = pageParam as IPageParam<string>;
			if (!typedPageParam || !("after" in typedPageParam) || !("first" in typedPageParam)) {
				throw new Error("PageParam should have properties 'after' and 'first'");
			}

			const paginatedResponse = await getAdvisorPerformance(
				locationId,
				commissionCycleId,
				payPeriodId,
				typedPageParam.first,
				typedPageParam.after
			);
			if (!paginatedResponse) {
				throw new Error("Error retrieving advisor performance");
			}

			return paginatedResponse;
		},
		getNextPageParam: buildGetNextPageParam(PAGINATION_SIZE),
		enabled: enabled && Boolean(locationId) && Boolean(payPeriodId),
		initialPageParam: { after: "", first: PAGINATION_SIZE },
		staleTime: Infinity,
		gcTime: 5000,
		fetchAllPages: true,
	});

	// Subscription
	const advisorPerformanceSubscription = getSubscription(["advisorPerformance", locationId, payPeriodId]);
	advisorPerformanceSubscription?.subscriptions?.onUpdateAdvisorPerformance?.setCallback(
		(result: GraphqlSubscriptionResponse<"onUpdateAdvisorPerformance", IAdvisorPerformanceResponse>) => {
			const data = result.data?.onUpdateAdvisorPerformance;
			if (data) {
				queryClient.invalidateQueries({ queryKey: ["advisorPerformance", locationId, payPeriodId] });
				setIngesting(false);
			}
		}
	);

	useMemo(() => {
		if (locationId && payPeriodId) {
			addSubscription(
				["advisorPerformance", locationId, payPeriodId],
				[
					{
						context: "onUpdateAdvisorPerformance",
						query: onUpdateAdvisorPerformance,
						variables: {
							locationId,
							payPeriodId,
						},
					},
				]
			);
		}
	}, [locationId, payPeriodId]);

	return { data: advisorPerformance, ...otherResults };
};
